import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ShieldAlert } from "lucide-react";
import Seo from "@/components/Seo";
import { Kicker, Field, inputCls } from "@/components/form";
import { api, formatApiError } from "@/lib/api";

export default function Schadenmeldung() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", policy_reference: "", claim_type: "", incident_date: "", description: "", gdpr_consent: false });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.gdpr_consent) { toast.error("Bitte stimmen Sie der Datenschutzerklärung zu."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/claims", form);
      toast.success(data.message);
      setForm({ name: "", email: "", phone: "", policy_reference: "", claim_type: "", incident_date: "", description: "", gdpr_consent: false });
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <div data-testid="schaden-page" className="bg-navy-900 min-h-screen">
      <Seo title="Schaden melden" description="Melden Sie unkompliziert Ihren Versicherungsschaden bei VM-FFM.DE." path="/schaden-melden" />
      <section className="pt-40 pb-24">
        <div className="max-w-3xl mx-auto px-6">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <Kicker>Schadenmeldung</Kicker>
            <h1 className="mt-6 text-4xl sm:text-5xl font-extrabold tracking-tighter text-white leading-none flex items-center gap-4"><ShieldAlert className="text-gold-500 hidden sm:block" size={44} strokeWidth={1.4} /> Schaden melden</h1>
            <p className="mt-6 text-slate-300 leading-relaxed">Teilen Sie uns Ihren Schaden mit. Wir kümmern uns umgehend um die Abwicklung mit Ihrer Versicherung.</p>
          </motion.div>
          <motion.form data-testid="schaden-form" onSubmit={submit} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }}
            className="mt-10 bg-navy-800 border border-white/5 rounded-md p-8 lg:p-10 space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field label="Name *"><input data-testid="schaden-name" required className={inputCls} value={form.name} onChange={set("name")} /></Field>
              <Field label="Telefon"><input data-testid="schaden-phone" className={inputCls} value={form.phone} onChange={set("phone")} /></Field>
            </div>
            <Field label="E-Mail *"><input data-testid="schaden-email" required type="email" className={inputCls} value={form.email} onChange={set("email")} /></Field>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field label="Vertrag / Versicherungsnr."><input data-testid="schaden-policy" className={inputCls} value={form.policy_reference} onChange={set("policy_reference")} placeholder="Optional" /></Field>
              <Field label="Schadenart *"><input data-testid="schaden-type" required className={inputCls} value={form.claim_type} onChange={set("claim_type")} placeholder="z. B. KFZ, Wasser, Einbruch" /></Field>
            </div>
            <Field label="Schadendatum *"><input data-testid="schaden-date" required type="date" className={inputCls} value={form.incident_date} onChange={set("incident_date")} /></Field>
            <Field label="Beschreibung *"><textarea data-testid="schaden-desc" required rows={5} className={inputCls} value={form.description} onChange={set("description")} placeholder="Was ist passiert?" /></Field>
            <p className="text-xs text-slate-500">Dokumenten- und Foto-Upload folgt in Kürze. Reichen Sie Belege gern per E-Mail nach.</p>
            <label className="flex items-start gap-3 text-xs text-slate-400 cursor-pointer">
              <input data-testid="schaden-gdpr" type="checkbox" checked={form.gdpr_consent} onChange={set("gdpr_consent")} className="mt-0.5 accent-gold-500 w-4 h-4" />
              <span>Ich stimme der Verarbeitung meiner Daten gemäß Datenschutzerklärung zu.</span>
            </label>
            <button data-testid="schaden-submit" disabled={loading} className="w-full bg-gold-500 text-navy-900 font-bold rounded-sm py-4 hover:bg-gold-300 transition-colors disabled:opacity-60">{loading ? "Wird gesendet…" : "Schaden melden"}</button>
          </motion.form>
        </div>
      </section>
    </div>
  );
}
